import { relativeDay } from '../actionQueueFormat';
import {
	type GetProductionOverviewQuery,
	OrderItemLifecycleStepEnum,
	ProductionStage,
	ProductionTone,
} from '@codezo/graphql';

export type ProductionOrder = GetProductionOverviewQuery['getProductionOverview']['orders'][number];
export type StageCount = GetProductionOverviewQuery['getProductionOverview']['stages'][number];

export const STAGE_META: Record<
	ProductionStage,
	{ label: string; color: string; frac: number; dashed?: boolean }
> = {
	[ProductionStage.Setup]: {
		label: 'Setup',
		color: 'var(--color-text-3)',
		frac: 0,
		dashed: true,
	},
	[ProductionStage.Sampling]: {
		label: 'Sampling',
		color: 'var(--color-status-info)',
		frac: 0.25,
	},
	[ProductionStage.Production]: {
		label: 'In production',
		color: 'var(--color-status-warn)',
		frac: 0.5,
	},
	[ProductionStage.ReadyToShip]: {
		label: 'Ready to ship',
		color: 'var(--color-status-success)',
		frac: 0.75,
	},
	[ProductionStage.InTransit]: { label: 'In transit', color: 'var(--color-accent)', frac: 1 },
};

export const STAGE_ORDER: ProductionStage[] = [
	ProductionStage.Setup,
	ProductionStage.Sampling,
	ProductionStage.Production,
	ProductionStage.ReadyToShip,
	ProductionStage.InTransit,
];

/** The order item lifecycle, as the order page shows it. */
export const LIFECYCLE: { step: OrderItemLifecycleStepEnum; label: string }[] = [
	{ step: OrderItemLifecycleStepEnum.Accepted, label: 'Accepted' },
	{ step: OrderItemLifecycleStepEnum.SamplePayment, label: 'Sample payment' },
	{ step: OrderItemLifecycleStepEnum.Sampling, label: 'Sampling' },
	{ step: OrderItemLifecycleStepEnum.Approval, label: 'Approval' },
	{ step: OrderItemLifecycleStepEnum.ProductPayment, label: 'Product payment' },
	{ step: OrderItemLifecycleStepEnum.Production, label: 'Production' },
	{ step: OrderItemLifecycleStepEnum.Delivery, label: 'Delivery' },
	{ step: OrderItemLifecycleStepEnum.Delivered, label: 'Delivered' },
];

export const TONE_CHIP: Record<ProductionTone, string> = {
	[ProductionTone.Danger]: 'bg-status-danger-soft text-status-danger',
	[ProductionTone.Warn]: 'bg-status-warn-soft text-status-warn',
	[ProductionTone.Info]: 'bg-status-info-soft text-status-info',
	[ProductionTone.Ok]: 'bg-status-success-soft text-status-success',
	[ProductionTone.Neutral]: 'bg-surface-fill text-text-3',
};

const DAY_MS = 24 * 60 * 60 * 1000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "Today" / "Tomorrow" / "3 days late" / "Mon 26 Oct" with a tone. */
export function deadlineLabel(iso: string | null | undefined, now = new Date()) {
	if (!iso) return { text: '—', tone: 'muted' as const };
	const due = new Date(iso);
	const days = Math.round((startOfDay(due).getTime() - startOfDay(now).getTime()) / DAY_MS);
	if (days < 0)
		return { text: `${-days} day${days === -1 ? '' : 's'} late`, tone: 'danger' as const };
	if (days === 0) return { text: 'Today', tone: 'warn' as const };
	if (days === 1) return { text: 'Tomorrow', tone: 'warn' as const };
	return {
		text: due.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }),
		tone: 'muted' as const,
	};
}

/** "Since 12 Oct" for the time-in-step column. */
/** "Started 28 Sep", "Ready since Mon 19", "Round 2 since yesterday" — the server names the event. */
export function sinceLabel(label: string, iso: string | null | undefined): string {
	if (!iso) return '—';
	return `${label} ${relativeDay(new Date(iso))}`;
}

export type GroupBy = 'stage' | 'turn' | 'deadline';

/** Deadline buckets in the order the list shows them. */
export function deadlineBucket(iso: string | null | undefined, now = new Date()) {
	if (!iso) return 'later';
	const days = Math.round(
		(startOfDay(new Date(iso)).getTime() - startOfDay(now).getTime()) / DAY_MS
	);
	if (days <= 0) return 'overdue';
	const endOfWeek = 7 - ((now.getDay() + 6) % 7); // days until next Monday
	if (days < endOfWeek) return 'week';
	if (days < endOfWeek + 7) return 'next';
	return 'later';
}

export const DEADLINE_GROUPS = [
	{ key: 'overdue', label: 'Overdue or due today', danger: true },
	{ key: 'week', label: 'Later this week' },
	{ key: 'next', label: 'Next week' },
	{ key: 'later', label: 'Later or no deadline' },
] as const;
